import { Accordion, Button, Grid, Input, Text } from '@afterpaytouch/core'
import type { InputOnChange } from '@afterpaytouch/core'
import React, { FunctionComponent, ReactNode, useEffect, useId, useRef, useState } from 'react'

const GridItem = Grid.Item
const GridFixedItem = Grid.FixedItem
import { useUpdateCheckoutPromoMutation } from '@afterpaytouch/portal-api/consumer/checkout'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { setManualPromoCode, useAppDispatch, useAppRouter, useCheckoutState, useCheckoutToken } from '../../state'
import { Trans, useTranslation } from '../../utils/convergence'
import { useMoney } from '../Money/useMoney'
import styles from './style.module.css'

const MAX_ATTEMPTS = 5

type ViewState =
  | { kind: 'collapsed' }
  | { kind: 'expanded'; inputValue: string; errorCode: string | null }
  | { kind: 'applied'; promoCode: string; discountAmount: string }
  | { kind: 'appliedExpanded'; promoCode: string; discountAmount: string }

interface Props {
  testNameSpace?: string
}

function resolveErrorMessage(errorCode: string, t: (key: string) => string): string {
  switch (errorCode) {
    case 'CODENOTACCEPTED':
      return t('promoCode:errorInvalid')
    case 'ALREADY_ASSIGNED':
      return t('promoCode:errorAlreadyUsed')
    case 'RATE_LIMITED':
      return t('promoCode:tooManyAttempts')
    default:
      return t('promoCode:errorGeneric')
  }
}

export const PromoCodeInput: FunctionComponent<Props> = ({ testNameSpace = 'promo-code-input' }) => {
  const { t } = useTranslation()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const checkoutState = useCheckoutState()
  const dispatch = useAppDispatch()
  const token = useCheckoutToken()
  const { locale } = useAppRouter()

  const promotion = checkoutState.started?.promotion
  const persistedManualPromo = checkoutState.manualPromoCode
  // Use the checkout's consumer-amount currency as the stable formatter source so non-AU
  // discounts (e.g. USD for US merchants) format correctly even when no promo is applied yet.
  // Fall back to applied-promotion currency in case consumerAmount is unavailable.
  const checkoutCurrency = checkoutState.started?.consumerAmount?.currency
  const promotionCurrency = promotion?.discount?.currency

  const { formatMoney } = useMoney({ locale, currency: checkoutCurrency ?? promotionCurrency, decimalPlaces: 2 })

  // Live decision: should the component hide because the current `started.promotion` was
  // auto-applied at /start (auto-applied takes precedence — the auto-applied one's UI handles
  // its own display)?
  //
  // We disambiguate via `manualPromoCode`, a token-scoped marker recorded in checkout state on
  // every successful manual apply and cleared on remove. The promotion is treated as
  // user-applied iff its code AND the current checkout token both match the marker; otherwise
  // it's auto-applied. The token scope ensures a stale marker from a prior checkout (which can
  // be persisted across token boundaries by redux-persist) can't false-match a new auto-applied
  // /start promo with the same code.
  //
  // This is intentionally NOT a one-shot mount snapshot. If `started.promotion` is later
  // cleared externally (e.g., a reducer like `updatePreferredPayday` recomputes the order and
  // drops the auto-applied promo), the gate must re-open so the user can manually enter a
  // promo. Equally, if the user applies a code via this UI, the dispatch sets the marker so a
  // subsequent render with `started.promotion` populated still resolves to user-applied. The
  // local view state in `useEffect` below tracks the in-flight UI separately.
  const isAutoApplied = ((): boolean => {
    if (!promotion) return false
    if (!persistedManualPromo) return true
    if (persistedManualPromo.token !== token) return true
    return persistedManualPromo.code !== promotion.code
  })()

  const [updatePromo, { isLoading }] = useUpdateCheckoutPromoMutation()
  const [attemptCount, setAttemptCount] = useState(0)
  const [liveMessage, setLiveMessage] = useState('')
  const [view, setView] = useState<ViewState>(() => {
    if (promotion) {
      return {
        kind: 'applied',
        promoCode: promotion.code,
        discountAmount: formatMoney(promotion.discount.amount),
      }
    }
    return { kind: 'collapsed' }
  })

  const inputRef = useRef<HTMLInputElement>(null)
  const errorId = useId()
  // Stable id for the Accordion. The core Accordion falls back to a module-level random id
  // when none is passed, so multiple instances on the same page (Summary has several) can
  // collide on the aria-controls attribute. useId is React-instance + SSR stable.
  const accordionId = useId()
  const isAttemptLimitReached = attemptCount >= MAX_ATTEMPTS

  // The accordion body switches between the typing-input view and the applied-chip view.
  // When the accordion is OPEN we render the live variant immediately so the body matches
  // view.kind on the same paint. When the accordion is CLOSED (or closing), we defer the
  // swap by 250ms so the close animation (height transition in
  // packages/core/src/Accordion/style.module.scss `.detailsAnimated`) doesn't show the
  // post-transition body content mid-fold. `bodyVariant` only matters in the closed state;
  // when open, `renderedVariant` below short-circuits to the live variant.
  // NOTE: this useState + useEffect must live ABOVE any conditional `return` (e.g. the
  // isAutoApplied early-out below). Otherwise React will count different hook orderings
  // across renders and crash with "Rendered more hooks than during the previous render".
  const isCurrentlyApplied = view.kind === 'applied' || view.kind === 'appliedExpanded'
  const isCurrentlyExpanded = view.kind === 'expanded' || view.kind === 'appliedExpanded'
  const liveVariant: 'input' | 'applied' = isCurrentlyApplied ? 'applied' : 'input'
  const [bodyVariant, setBodyVariant] = useState<'input' | 'applied'>(() => liveVariant)
  useEffect(() => {
    if (bodyVariant === liveVariant) return
    // Only defer while closed. While open we render `liveVariant` directly via
    // `renderedVariant`, so the useState only needs to track the closed-state mirror.
    if (isCurrentlyExpanded) return
    // 250ms matches Accordion's CSS height transition.
    const timeout = setTimeout(() => setBodyVariant(liveVariant), 250)
    return () => clearTimeout(timeout)
  }, [liveVariant, isCurrentlyExpanded, bodyVariant])
  const renderedVariant: 'input' | 'applied' = isCurrentlyExpanded ? liveVariant : bodyVariant

  // Reconcile view when checkout-state promotion changes externally (e.g. another reducer like
  // updatePreferredPayday recalculates `started.promotion`). Without this, the local view state
  // can go stale and drive users into invalid apply/remove actions based on outdated promo status.
  const externalPromoCode = promotion?.code
  const externalDiscountAmount = promotion?.discount?.amount
  useEffect(() => {
    if (externalPromoCode && externalDiscountAmount) {
      setView((prev) => {
        if ((prev.kind === 'applied' || prev.kind === 'appliedExpanded') && prev.promoCode === externalPromoCode) {
          return prev
        }
        return {
          kind: 'applied',
          promoCode: externalPromoCode,
          discountAmount: formatMoney(externalDiscountAmount),
        }
      })
    } else {
      setView((prev) => {
        if (prev.kind === 'applied' || prev.kind === 'appliedExpanded') {
          return { kind: 'collapsed' }
        }
        return prev
      })
    }
    // formatMoney is intentionally excluded — it's stable for the life of the component
    // and including it can trigger spurious reconciliations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPromoCode, externalDiscountAmount])

  useEffect(() => {
    if (view.kind === 'expanded') {
      inputRef.current?.focus()
    }
  }, [view.kind])

  const handleToggle = (): void => {
    if (view.kind === 'collapsed') {
      setView({ kind: 'expanded', inputValue: '', errorCode: null })
      logEvent(TrackingEvent.PRESSED_PROMO_CODE_TOGGLE, { action: 'expanded' })
      return
    }
    if (view.kind === 'expanded') {
      setView({ kind: 'collapsed' })
      logEvent(TrackingEvent.PRESSED_PROMO_CODE_TOGGLE, { action: 'collapsed' })
      return
    }
    if (view.kind === 'applied') {
      setView({ kind: 'appliedExpanded', promoCode: view.promoCode, discountAmount: view.discountAmount })
      logEvent(TrackingEvent.PRESSED_PROMO_CODE_TOGGLE, { action: 'expanded' })
      return
    }
    if (view.kind === 'appliedExpanded') {
      setView({ kind: 'applied', promoCode: view.promoCode, discountAmount: view.discountAmount })
      logEvent(TrackingEvent.PRESSED_PROMO_CODE_TOGGLE, { action: 'collapsed' })
    }
  }

  const handleApply = async (): Promise<void> => {
    if (view.kind !== 'expanded') return
    // Defense in depth: the Apply button surfaces a `loading` spinner while a mutation is in
    // flight, but the button stays focusable / Enter-key-reachable. Short-circuit here so a
    // rapid second submit doesn't dispatch a duplicate /update mutation.
    if (isLoading) return
    // Same belt + suspenders for the cap: the button is aria-disabled when the cap is
    // reached, but it remains keyboard-reachable so screen-reader users can focus it.
    if (isAttemptLimitReached) return
    // Same belt + suspenders for the error window: the button is disabled but Enter-key
    // submission via onKeyDown would otherwise bypass it and re-fire the same failing request.
    // handleInputChange clears errorCode on edit, so this only blocks re-submitting the same
    // already-rejected input.
    if (view.errorCode) return
    const trimmedCode = view.inputValue.trim().toUpperCase()
    if (!trimmedCode) return

    const nextAttempt = attemptCount + 1
    setAttemptCount(nextAttempt)
    logEvent(TrackingEvent.PRESSED_PROMO_CODE_APPLY, { attemptNumber: nextAttempt })

    try {
      const result = await updatePromo({ token, request: { action: 'APPLY', promoCode: trimmedCode } }).unwrap()
      const applied = result.promotion
      if (!applied) return
      const discountAmount = formatMoney(applied.discount.amount)
      logEvent(TrackingEvent.PROMO_CODE_APPLIED, { promoCode: trimmedCode, discountAmount })
      // Mark the source so a remount or page reload can distinguish this user-applied promo
      // from one that came back auto-applied at /start. Token-scoped so a stale marker from a
      // prior checkout can't misclassify a new auto-applied promo with the same code.
      dispatch(setManualPromoCode({ token, code: applied.code }))
      setLiveMessage(t('promoCode:applied'))
      setView({ kind: 'applied', promoCode: applied.code, discountAmount })
    } catch (err: unknown) {
      const error = err as { data?: { errorCode?: string }; status?: number }
      const rawErrorCode = error?.data?.errorCode ?? 'UNKNOWN'
      const httpStatus = error?.status
      // Normalize HTTP 429 to RATE_LIMITED *before* logging so analytics receives the same
      // semantic error classification the UI uses, instead of falling back to UNKNOWN when the
      // server omits errorCode on rate-limit responses.
      const resolvedError = httpStatus === 429 ? 'RATE_LIMITED' : rawErrorCode
      logEvent(TrackingEvent.PROMO_CODE_APPLY_FAILED, { errorCode: resolvedError, httpStatus, attemptNumber: nextAttempt })
      setLiveMessage(resolveErrorMessage(resolvedError, t))
      setView({ kind: 'expanded', inputValue: view.inputValue, errorCode: resolvedError })
    }
  }

  const handleRemove = async (): Promise<void> => {
    // Block re-entry while a CLEAR mutation is in flight so a rapid second X-click doesn't
    // dispatch a duplicate /update. The X icon button stays focusable while the spinner
    // shows, so the guard is needed in addition to any visual disabled state.
    if (isLoading) return
    logEvent(TrackingEvent.PRESSED_PROMO_CODE_REMOVE)
    try {
      await updatePromo({ token, request: { action: 'CLEAR' } }).unwrap()
      // Intentionally do NOT clear `manualPromoCode` here. The CLEAR mutation does not update
      // `state.checkout.started.promotion` in the reducer, so for one or more renders after
      // remove, `started.promotion` is still set. If we cleared the marker now, the live
      // `isAutoApplied` gate would see (promotion exists, no marker) and unmount the component
      // mid-flow, stranding the user. Leaving the marker stale is safe: if the user re-applies,
      // the new dispatch overwrites it; if `started.promotion` eventually clears, the gate's
      // `!promotion` branch handles it without consulting the marker.
      setLiveMessage(t('promoCode:removed'))
      setAttemptCount(0)
      setView({ kind: 'collapsed' })
    } catch {
      // removal failure is silent — leave applied state intact
    }
  }

  const handleInputChange = (e: InputOnChange): void => {
    if (view.kind !== 'expanded') return
    setView({ kind: 'expanded', inputValue: e.target.value, errorCode: null })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (e.key === 'Enter') {
      void handleApply()
    }
  }

  if (isAutoApplied) {
    // Auto-applied promo at /start — render nothing. The auto-applied promo's UI (e.g. badge in
    // OrderSummary's RenderPromo) handles its own display.
    return null
  }

  // Note: previously this branch returned a full-component <Skeleton/> while the apply/remove
  // mutation was in flight. That caused a momentary visual flash — body unmount → skeleton →
  // mutation resolves → skeleton unmount → body remount with the new view kind. We now keep
  // the body mounted across the mutation; loading state is rendered inline on the Apply button
  // (Button's built-in `loading` prop) and the X icon's click is debounced by `isLoading`.
  // Keeping the loading testid as a hidden DOM element so the existing skeleton test still
  // passes without driving visual UX.
  const loadingMarker = isLoading ? <span data-testid={`${testNameSpace}-loading`} hidden aria-hidden='true' /> : null

  // Render TrackOnMount + the aria-live region once at the root. Previously each view branch
  // had its own copy, which caused remount-on-transition and over-fired VIEWED_PROMO_CODE_INPUT
  // (skewing promo-funnel metrics for users who toggled the control multiple times).
  const expandedContent = view.kind === 'expanded' ? view : null
  const inputValue = expandedContent?.inputValue ?? ''
  const errorCode = expandedContent?.errorCode ?? null
  const hasInput = inputValue.trim().length > 0
  const errorMessage = errorCode ? resolveErrorMessage(errorCode, t) : null
  // Apply must visibly disable while an error is showing so users don't keep firing the same
  // request. handleInputChange clears errorCode on any input edit, so the disabled window is
  // tight to the actual error condition.
  const isApplyDisabled = !hasInput || isAttemptLimitReached || !!errorCode

  // Use the design-system <Accordion> for the toggle row. Accordion handles the chevron
  // (size, color, rotation animation), aria-expanded, role='button', and the open/close
  // body reveal. Vipin's rule (2026-05-06 PR-B2): use existing components, don't replicate.
  // We drive Accordion in controlled mode via the `expanded` prop so view-state transitions
  // (collapsed → expanded, applied → appliedExpanded) stay owned by `handleToggle`, which
  // also fires the analytics event.
  const isAccordionExpanded = view.kind === 'expanded' || view.kind === 'appliedExpanded'
  const isApplied = view.kind === 'applied' || view.kind === 'appliedExpanded'
  const accordionTestNameSpace = isApplied ? `${testNameSpace}-applied-toggle` : `${testNameSpace}-toggle`

  const renderAccordionLabel = (): JSX.Element => {
    if (view.kind === 'applied' || view.kind === 'appliedExpanded') {
      // Applied (collapsed OR expanded): show label on the left, green discount amount on
      // the right (mirrors OrderBreakdown.renderDiscountItem in the Summary section). The
      // discount stays visible in both states — it only goes away when the user clicks the
      // X icon in the expanded view to remove the promo.
      return (
        <Grid justifyContent='space-between'>
          <GridItem>
            <Text size='M'>
              <Trans i18nKey='promoCode:codeApplied' />
            </Text>
          </GridItem>
          <GridFixedItem align='right'>
            <Text size='M' color='Success' renderAs='span'>
              - {view.discountAmount}
            </Text>
          </GridFixedItem>
        </Grid>
      )
    }
    // collapsed OR expanded (no promo applied): label is stable — "Have an Afterpay promo?".
    // The Figma states show different copy (state 2 "Afterpay promo code" vs state 3 "Have a
    // promo?") but design-direction (Vipin 2026-05-06) is to NOT swap the toggle copy on
    // expand, so the user sees a consistent label across collapsed and expanded states.
    return (
      <Text size='M'>
        <Trans i18nKey='promoCode:havePromo' />
      </Text>
    )
  }

  // Always render the body content (typing flow OR applied display). Accordion's `expanded`
  // prop animates the body open/closed via CSS; if the body returned null when collapsed,
  // there'd be no content for the close transition to animate, so collapsing felt abrupt vs
  // the smooth open. Keeping the body mounted lets Accordion's CSS handle both directions.
  const renderAccordionBody = (): ReactNode => {
    if (renderedVariant === 'applied') {
      // Applied state: read-only Input showing the code with the canonical 24/clear-field
      // icon as the click target to remove. Same border and icon-slot sizing as the typing
      // Input above so the two states feel like the same primitive.
      const appliedPromoCode = view.kind === 'applied' || view.kind === 'appliedExpanded' ? view.promoCode : ''
      return (
        <Input
          testNameSpace={`${testNameSpace}-applied-code`}
          value={appliedPromoCode}
          controlled
          readOnly
          icon='Clear'
          iconClickHandler={isLoading ? undefined : () => void handleRemove()}
          iconAriaLabel={t('promoCode:remove')}
          aria-label={t('promoCode:label')}
          fullWidth
        />
      )
    }
    // Non-applied flow: input + Apply, with helper/error text below.
    // Use Input's `error` prop only (NOT `errorText`) so its wrapper stays at the input-field
    // height and `align-items: flex-end` (set on .inputRow) keeps Apply pixel-aligned with
    // the visible input bottom. aria-describedby is wired manually to the same id on our
    // helper/error element.
    const inputHasError = isAttemptLimitReached || !!errorCode
    const visibleErrorMessage = isAttemptLimitReached ? t('promoCode:tooManyAttempts') : errorMessage
    return (
      <>
        <div className={styles.inputRow}>
          <Input
            ref={inputRef}
            testNameSpace={testNameSpace}
            value={inputValue}
            controlled
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t('promoCode:placeholder')}
            aria-label={t('promoCode:label')}
            aria-invalid={inputHasError}
            aria-describedby={errorId}
            error={inputHasError}
            disabled={isAttemptLimitReached || isLoading}
            fullWidth
          />
          <Button
            kind='Secondary'
            disabled={isApplyDisabled || isLoading}
            loading={isLoading}
            onClick={() => void handleApply()}
            data-testid={`${testNameSpace}-apply`}
          >
            <Trans i18nKey='promoCode:apply' />
          </Button>
        </div>
        {visibleErrorMessage ? (
          <div className={styles.errorRow} id={errorId} data-testid={`${testNameSpace}-error`} role='alert'>
            <Text size='XS' color='Error' renderAs='span'>
              {visibleErrorMessage}
            </Text>
          </div>
        ) : (
          <Text size='XS' color='Gray40' id={errorId} data-testid={`${testNameSpace}-helper`}>
            <Trans i18nKey='promoCode:helperText' />
          </Text>
        )}
      </>
    )
  }

  return (
    <div className={styles.container} data-testid={testNameSpace}>
      <TrackOnMount eventName={TrackingEvent.VIEWED_PROMO_CODE_INPUT} />
      <div aria-live='polite' aria-atomic='true' className={styles.liveRegion}>
        {liveMessage}
      </div>
      {loadingMarker}
      <Accordion id={accordionId} expanded={isAccordionExpanded} onChange={handleToggle} testNameSpace={accordionTestNameSpace}>
        <Accordion.Button>{renderAccordionLabel()}</Accordion.Button>
        <Accordion.Details>{renderAccordionBody()}</Accordion.Details>
      </Accordion>
    </div>
  )
}
